/**
 * Colour for everything fx draws: occupier hues, viewer hues, and the theme
 * tokens read back from tokens.css.
 *
 * Occupier hues walk the golden angle from the door's teal and skip three
 * bands, so an occupier is never mistaken for a refusal (red), an ask (amber)
 * or the selection teal. There is no palette cap: the studio had six colours
 * and a seventh planner had none. Viewer (peer) hues are for rings and trails
 * only, never fills. Pure, apart from `readTheme`, which reads computed styles.
 */

export type Rgb = readonly [number, number, number];   // sRGB, 0..1

// ---------- hue bands and the occupier walk ----------

/** Hue bands an occupier may never take (OKLCH degrees, inclusive). */
export const BANDS: readonly (readonly [number, number, string])[] = [
  [15, 40, 'refuse'],
  [60, 95, 'ask'],
  [165, 195, 'mine'],
];

export const inBand = (h: number): boolean => {
  const x = ((h % 360) + 360) % 360;
  return BANDS.some(([lo, hi]) => x >= lo && x <= hi);
};

const GOLDEN = 137.508;
const walk: number[] = [];
let nextStep = 0;

/** The i-th occupier hue: the i-th golden-angle step from 178 that lands outside every band. */
export const occupierHue = (i: number): number => {
  const n = Math.max(0, Math.floor(i));
  while (walk.length <= n) {
    const h = (178 + GOLDEN * nextStep++) % 360;
    if (!inBand(h)) walk.push(Math.round(h * 1000) / 1000);
  }
  return walk[n] as number;
};

/** A stable hue for a string (a peer's sid when its colour is unknown), outside the bands. */
export const hashHue = (s: string): number => {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) h = Math.imul(h ^ s.charCodeAt(i), 0x01000193);
  return occupierHue((h >>> 0) % 97);
};

// ---------- colour maths ----------

const toLinear = (c: number): number => (c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);
const toGamma = (c: number): number => (c <= 0.0031308 ? 12.92 * c : 1.055 * c ** (1 / 2.4) - 0.055);

const oklabToLinear = (L: number, a: number, b: number): [number, number, number] => {
  const l_ = L + 0.3963377774 * a + 0.2158037573 * b;
  const m_ = L - 0.1055613458 * a - 0.0638541728 * b;
  const s_ = L - 0.0894841775 * a - 1.291485548 * b;
  const l = l_ ** 3, m = m_ ** 3, s = s_ ** 3;
  return [
    4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
    -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
    -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s,
  ];
};

const inGamut = (c: readonly number[]): boolean => c.every((v) => v >= -1e-4 && v <= 1 + 1e-4);

/** oklch(L C h) as sRGB. Out-of-gamut colours keep L and h and give up chroma. */
export const oklch = (L: number, C: number, h: number): Rgb => {
  const rad = (h * Math.PI) / 180;
  let lin = oklabToLinear(L, C * Math.cos(rad), C * Math.sin(rad));
  if (!inGamut(lin)) {
    let lo = 0, hi = C;
    for (let i = 0; i < 20; i++) {
      const mid = (lo + hi) / 2;
      const t = oklabToLinear(L, mid * Math.cos(rad), mid * Math.sin(rad));
      if (inGamut(t)) { lo = mid; lin = t; } else hi = mid;
    }
    if (!inGamut(lin)) lin = oklabToLinear(L, lo * Math.cos(rad), lo * Math.sin(rad));
  }
  const c = (v: number) => Math.min(1, Math.max(0, toGamma(Math.min(1, Math.max(0, v)))));
  return [c(lin[0]), c(lin[1]), c(lin[2])];
};

/** sRGB → OKLCH, for a viewer's hex colour. */
export const toOklch = (rgb: Rgb): { L: number; C: number; h: number } => {
  const r = toLinear(rgb[0]), g = toLinear(rgb[1]), b = toLinear(rgb[2]);
  const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
  const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
  const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);
  const L = 0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s;
  const A = 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s;
  const B = 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s;
  const h = (Math.atan2(B, A) * 180) / Math.PI;
  return { L, C: Math.hypot(A, B), h: (h + 360) % 360 };
};

/** '#rgb', '#rrggbb', 'rgb(r g b / a)' or 'rgb(r, g, b)' → sRGB (alpha ignored); null when unreadable. */
export const parseColor = (text: string): Rgb | null => {
  const t = text.trim().toLowerCase();
  const hex = /^#([0-9a-f]{3}|[0-9a-f]{6})$/.exec(t);
  if (hex) {
    const d = hex[1] as string;
    const full = d.length === 3 ? d.split('').map((c) => c + c).join('') : d;
    const n = Number.parseInt(full, 16);
    return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255];
  }
  const fn = /^rgba?\(\s*([\d.]+)[\s,]+([\d.]+)[\s,]+([\d.]+)/.exec(t);
  if (fn) return [Number(fn[1]) / 255, Number(fn[2]) / 255, Number(fn[3]) / 255];
  const ok = /^oklch\(\s*([\d.]+)(%?)\s+([\d.]+)\s+([\d.]+)/.exec(t);
  if (ok) return oklch(Number(ok[1]) / (ok[2] ? 100 : 1), Number(ok[3]), Number(ok[4]));
  return null;
};

export const toHex = (c: Rgb): string =>
  '#' + c.map((v) => Math.round(Math.min(1, Math.max(0, v)) * 255).toString(16).padStart(2, '0')).join('');

/** WCAG relative luminance. */
export const luminance = (c: Rgb): number => 0.2126 * toLinear(c[0]) + 0.7152 * toLinear(c[1]) + 0.0722 * toLinear(c[2]);

/** WCAG contrast ratio, 1..21. */
export const contrast = (a: Rgb, b: Rgb): number => {
  const la = luminance(a), lb = luminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
};

export const mixRgb = (a: Rgb, b: Rgb, t: number): Rgb => [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t];

/** Pull a colour toward its own grey: `amount` 0 keeps it, 1 is fully grey. */
export const desaturate = (c: Rgb, amount: number): Rgb => {
  const y = 0.2126 * c[0] + 0.7152 * c[1] + 0.0722 * c[2];
  return mixRgb(c, [y, y, y], amount);
};

// ---------- the theme, as fx needs it ----------

export interface Theme {
  readonly dark: boolean;
  readonly bg: Rgb; readonly bg2: Rgb; readonly line: Rgb; readonly ink: Rgb; readonly muted: Rgb;
  readonly freeRing: Rgb; readonly freeFill: Rgb;
  readonly mine: Rgb; readonly ask: Rgb; readonly refuse: Rgb; readonly focus: Rgb; readonly foreign: Rgb;
  readonly occL: number; readonly occC: number; readonly viewerL: number; readonly viewerC: number;
  /** Motion tokens, raw (tween.ts parses them). */
  readonly motion: Readonly<Record<string, string>>;
}

export const COLOR_TOKENS = ['--bg', '--bg-2', '--line', '--ink', '--muted', '--bit-free-ring', '--bit-free-fill',
  '--mine', '--ask', '--refuse', '--focus', '--foreign'] as const;
export const MOTION_TOKENS = ['--t-press', '--t-quick', '--t-travel', '--t-reveal', '--t-dive',
  '--ease-std', '--ease-out', '--ease-io', '--ease-dive', '--stagger-bit', '--stagger-cap'] as const;

/** The dark tokens, as tokens.css states them: the theme before any stylesheet is read. */
export const DARK_VARS: Readonly<Record<string, string>> = {
  '--bg': '#07090C', '--bg-2': '#0B0F14', '--line': '#1E2833', '--ink': '#E7ECF2', '--muted': '#93A0AE',
  '--bit-free-ring': '#5B6878', '--bit-free-fill': '#121922', '--mine': '#2DD4BF', '--ask': '#F5B544',
  '--refuse': '#FF6B5E', '--focus': '#8AB4FF', '--foreign': '#6C7A89',
  '--occ-l': '0.80', '--occ-c': '0.15', '--viewer-l': '0.86', '--viewer-c': '0.10',
  '--t-press': '90ms', '--t-quick': '160ms', '--t-travel': '420ms', '--t-reveal': '640ms', '--t-dive': '900ms',
  '--ease-std': 'cubic-bezier(.2,0,0,1)', '--ease-out': 'cubic-bezier(.16,1,.3,1)',
  '--ease-io': 'cubic-bezier(.65,0,.35,1)', '--ease-dive': 'cubic-bezier(.7,0,.2,1)',
  '--stagger-bit': '14ms', '--stagger-cap': '220ms',
};

/** A Theme from a lookup of custom properties; anything missing or unreadable falls back to the dark tokens. */
export const themeFrom = (get: (name: string) => string | undefined): Theme => {
  const raw = (name: string): string => {
    const v = get(name)?.trim();
    return v ? v : (DARK_VARS[name] ?? '');
  };
  const col = (name: string): Rgb => parseColor(raw(name)) ?? (parseColor(DARK_VARS[name] ?? '#000') as Rgb);
  const num = (name: string): number => {
    const n = Number.parseFloat(raw(name));
    return Number.isFinite(n) ? n : Number.parseFloat(DARK_VARS[name] ?? '0');
  };
  const bg = col('--bg');
  const motion: Record<string, string> = {};
  for (const m of MOTION_TOKENS) motion[m] = raw(m);
  return {
    dark: luminance(bg) < 0.2,
    bg, bg2: col('--bg-2'), line: col('--line'), ink: col('--ink'), muted: col('--muted'),
    freeRing: col('--bit-free-ring'), freeFill: col('--bit-free-fill'),
    mine: col('--mine'), ask: col('--ask'), refuse: col('--refuse'), focus: col('--focus'), foreign: col('--foreign'),
    occL: num('--occ-l'), occC: num('--occ-c'), viewerL: num('--viewer-l'), viewerC: num('--viewer-c'),
    motion,
  };
};

export const DARK_THEME: Theme = themeFrom(() => undefined);

/** The live theme, from the document's computed custom properties. */
export const readTheme = (el: Element = document.documentElement): Theme => {
  try {
    const cs = getComputedStyle(el);
    return themeFrom((name) => cs.getPropertyValue(name));
  } catch {
    return DARK_THEME;
  }
};

/** Occupier fill for a hue under a theme; cached, since the packer asks per row. */
const occCache = new Map<string, Rgb>();
export const occupierRgb = (theme: Theme, hue: number): Rgb => {
  const k = `${theme.occL}|${theme.occC}|${hue}`;
  let c = occCache.get(k);
  if (!c) {
    c = oklch(theme.occL, theme.occC, hue);
    if (occCache.size > 4096) occCache.clear();
    occCache.set(k, c);
  }
  return c;
};

/** A peer's ring colour: its viewer hue at the viewer lightness, never an occupier fill. */
export const viewerRgb = (theme: Theme, hue: number): Rgb => oklch(theme.viewerL, theme.viewerC, hue);

/** A viewer's '#rrggbb' as a hue. */
export const viewerHue = (color: string): number => {
  const c = parseColor(color);
  return c ? Math.round(toOklch(c).h) : 0;
};

/** Source tints of the call → effect grammar (§4.4). */
export const tints = (theme: Theme) => ({
  me: [1, 1, 1] as Rgb,
  meEnd: theme.mine,
  director: oklch(theme.dark ? 0.9 : 0.55, 0.09, 300),
  scenario: oklch(theme.dark ? 0.9 : 0.6, 0.08, 80),
  roadOut: theme.ask,
  roadBack: theme.mine,
  unknown: theme.dark ? ([0.82, 0.85, 0.88] as Rgb) : ([0.35, 0.37, 0.4] as Rgb),
  error: theme.foreign,
});

// ---------- tokens.css as text (tests, and the contrast audit) ----------

const blockBody = (css: string, selector: RegExp): string => {
  const m = selector.exec(css);
  if (!m) return '';
  let depth = 0, start = -1;
  for (let i = m.index; i < css.length; i++) {
    const ch = css[i];
    if (ch === '{') { if (depth === 0) start = i + 1; depth++; }
    else if (ch === '}') { depth--; if (depth === 0) return css.slice(start, i); }
  }
  return '';
};

const declarations = (body: string): Record<string, string> => {
  const out: Record<string, string> = {};
  const clean = body.replace(/\/\*[\s\S]*?\*\//g, '');
  for (const m of clean.matchAll(/(--[a-z0-9-]+)\s*:\s*([^;]+);/gi)) out[m[1] as string] = (m[2] as string).trim();
  return out;
};

/** The dark and light token sets declared in tokens.css: dark on :root (the default), light when the page says so. */
export const parseTokensCss = (css: string): { light: Record<string, string>; dark: Record<string, string> } => {
  const dark = declarations(blockBody(css, /(^|\n)\s*:root\s*\{/));
  const light = { ...dark, ...declarations(blockBody(css, /:root\[data-theme=light\]\s*\{/)) };
  return { light, dark };
};
